/**
 * Roteador mínimo baseado na History API (ver docs/01-arquitetura.md, decisão "roteador próprio").
 * API pública: <RouterProvider>, useLocation(), navigate(), <Link>, matchPath().
 */
import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes, type ReactNode } from "react";

interface Location {
  pathname: string;
  search: string;
  hash: string;
}

const RouterContext = createContext<Location>({ pathname: "/", search: "", hash: "" });

function current(): Location {
  return { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash };
}

const listeners = new Set<() => void>();

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  listeners.forEach((l) => l());
  window.scrollTo({ top: 0 });
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Location>(current);
  useEffect(() => {
    const update = () => setLoc(current());
    listeners.add(update);
    window.addEventListener("popstate", update);
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);
  return <RouterContext.Provider value={loc}>{children}</RouterContext.Provider>;
}

export function useLocation(): Location {
  return useContext(RouterContext);
}

export function useSearchParams(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

/** matchPath("/org/:orgId/atletas/:id", "/org/1/atletas/2") -> { orgId: "1", id: "2" } */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const s = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) params[p[i].slice(1)] = decodeURIComponent(s[i]);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };

export function Link({ to, onClick, children, ...rest }: LinkProps) {
  return (
    <a
      href={to}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
